import { computed, inject } from '@angular/core';
import { AuthSessionStore } from '@indutrust/auth-data';
import {
  createFactoryFormValue,
  factoryFormSchema,
  getFactoryEditPayload,
} from '@indutrust/factory-util';
import {
  withEntityFormFromSource,
  withEntityId,
  withSignalForm,
} from '@indutrust/shared-util';
import { signalStore, withComputed, withMethods } from '@ngrx/signals';
import { FactoryStore } from '../../factory/factory.store';
import { FactoryImagesStore } from '../../image/+state/factory-images.store';

/**
 * Scoped store owning the main factory edit signal form lifecycle.
 *
 * Syncs the form model from {@link FactoryStore.entity} and exposes persona
 * predicates for the action bar, including {@link canMutateContent} for R5
 * Viewer read-only gating. Region catalogue loading stays in the
 * feature component to avoid a `factory-data` ↔ `map-data` cycle.
 *
 * When {@link FactoryImagesStore.galleryOrderDirty} is true the save payload
 * includes `gallery_image_ids` so the gallery display order is persisted in
 * the same PATCH request as the other factory fields.
 *
 * @example
 * const formStore = inject(FactoryEditFormStore);
 * formStore.setEntityId(7);
 * formStore.patch();
 */
export const FactoryEditFormStore = signalStore(
  withEntityId(),
  withSignalForm({
    createEmptyValue: createFactoryFormValue,
    schema: factoryFormSchema,
  }),
  withEntityFormFromSource({
    entityStore: () => inject(FactoryStore),
    mapEntityToFormValue: (entity) => createFactoryFormValue(entity),
  }),
  withComputed((store, authStore = inject(AuthSessionStore)) => ({
    canMutateContent: computed(
      () => authStore.isAdmin() || authStore.isTeamManager()
    ),
    canPublish: computed(() => authStore.isAdmin()),
    canSubmitForApproval: computed(() => {
      if (authStore.isAdmin()) {
        return true;
      }
      return authStore.isSupplier() && authStore.isTeamManager();
    }),
    countryId: computed(() => store.formModel().country_id),
  })),
  withMethods(
    (
      store,
      factoryStore = inject(FactoryStore),
      imagesStore = inject(FactoryImagesStore)
    ) => ({
      patch() {
        if (!store.canMutateContent()) {
          return;
        }
        const entityId = store.entityId();
        if (entityId === null || entityId <= 0) {
          return;
        }
        const payload = getFactoryEditPayload(store.formModel());
        if (imagesStore.galleryOrderDirty()) {
          payload.gallery_image_ids = imagesStore
            .orderedImages()
            .map((img) => img.id);
        }
        factoryStore.patch(entityId, payload);
      },
    })
  )
);
